import { firestore } from 'firebase/app'
import { FirestoreQuery } from '../Firestore/FirestoreQuery'
import { Company } from './Company'
import { CompanyTypeEnum } from './CompanyTypeEnum'
import { CompanyMapper } from './CompanyMapper'
import { CompanyStatusEnum } from './CompanyStatusEnum'

export class CompanyCollection {
  protected collection: string = 'company'

  constructor(protected db: firestore.Firestore, protected batch?: firestore.WriteBatch) {}

  watchCompaniesByType(
    type: CompanyTypeEnum,
    cb?: (list: Company[], unsub?: (() => void) | undefined) => void
  ): Company[] {
    const query = this.initQuery()
    query.setQuery(this.getCompaniesByTypeQuery(query, type))
    return query.watchList(cb)
  }

  watchCompaniesByAdminCompany(
    adminCompanyId: string,
    cb?: (list: Company[], unsub?: (() => void) | undefined) => void
  ): Company[] {
    const query = this.initQuery()
    query.setQuery(this.getCompaniesByFieldValue(query, 'adminCompanyId', adminCompanyId))
    return query.watchList(cb)
  }

  list(order?: [string, 'asc' | 'desc'][]): Promise<Company[]> {
    const query = this.initQuery()
    if (order && order.length) {
      query.setQuery(
        order.reduce(
          (q, o) => q.orderBy(...o),
          query.collection(this.collection) as firestore.Query
        )
      )
    }
    return query.list()
  }

  listCompaniesByType(type: CompanyTypeEnum): Promise<Company[]> {
    const query = this.initQuery()
    query.setQuery(this.getCompaniesByTypeQuery(query, type))
    return query.list()
  }

  listActiveCompaniesByType(type: CompanyTypeEnum): Promise<Company[]> {
    const query = this.initQuery()
    query.setQuery(this.getActiveCompaniesByTypeQuery(query, type))
    return query.list()
  }

  async listCompaniesByUser(userId: string): Promise<Company[]> {
    const userQuery = this.initQuery()
    const adminQuery = this.initQuery()
    userQuery.setQuery(this.getCompaniesByArrayContains(userQuery, 'users', userId))
    adminQuery.setQuery(this.getCompaniesByArrayContains(adminQuery, 'admins', userId))

    const companies = [...(await userQuery.list()), ...(await adminQuery.list())]

    return [...companies.reduce((acc, cur) => acc.set(cur.id, cur), new Map()).values()]
  }

  listCompaniesByFieldValue(field: string, value: string, active?: boolean): Promise<Company[]> {
    const query = this.initQuery()
    active
      ? query.setQuery(this.getActiveCompaniesByFieldValue(query, field, value))
      : query.setQuery(this.getCompaniesByFieldValue(query, field, value))

    return query.list()
  }

  listCompaniesByAdminCompany(adminCompanyId: string): Promise<Company[]> {
    const query = this.initQuery()
    query.setQuery(this.getCompaniesByFieldValue(query, 'adminCompanyId', adminCompanyId))
    return query.list()
  }

  listActiveCompaniesByAdminCompany(adminCompanyId: string): Promise<Company[]> {
    const query = this.initQuery()
    query.setQuery(this.getActiveCompaniesByFieldValue(query, 'adminCompanyId', adminCompanyId))
    return query.list()
  }

  watchCompany(id: string): { data: Company | null } {
    const query = this.initQuery()
    return query.watch(id)
  }

  getCompany(id: string): Promise<Company | null> {
    const query = this.initQuery()
    return query.find(id)
  }

  async getCompanyBySevenFiftyId(sevenFiftyId: number): Promise<Company | null> {
    const query = this.initQuery()
    query.setQuery(this.getCompaniesByFieldValue(query, 'externalIds.sevenFifty', sevenFiftyId).limit(1))
    const companies = await query.list()
    return companies.length ? companies[0] : null
  }

  async getVendorCompanyBySevenFiftyId(sevenFiftyId: number): Promise<Company | null> {
    const query = this.initQuery()
    query.setQuery(this.getVendorCompanyBySevenFiftyIdQuery(query, sevenFiftyId))
    const companies = await query.list()
    return companies.length ? companies[0] : null
  }

  async getCompanyBySubscription(subscriptionId: number): Promise<Company | null> {
    const query = this.initQuery()
    query.setQuery(this.getCompaniesByFieldValue(query, 'subscriptionId', subscriptionId).limit(1))
    const companies = await query.list()
    return companies.length ? companies[0] : null
  }

  saveCompany(company: Company): Promise<Company> {
    company.name = company.name.trim()
    if(company.identifier) {
      company.identifier = company.identifier.trim()
    }

    const query = this.initQuery()
    return query.save(company)
  }

  delete(company: Company) {
    const query = this.initQuery();
    return query.delete(company)
  }

  protected initQuery(): FirestoreQuery<Company> {
    const query = new FirestoreQuery<Company>(this.db, new CompanyMapper())
    query.collection(this.collection)

    return query
  }

  protected getCompaniesByTypeQuery(query: FirestoreQuery<Company>, type: CompanyTypeEnum) {
    return this.getCompaniesByFieldValue(query, 'type', type)
  }

  protected getActiveCompaniesByTypeQuery(query: FirestoreQuery<Company>, type: CompanyTypeEnum) {
    return this.getCompaniesByFieldValue(query, 'type', type).where('status', '==', CompanyStatusEnum.ACTIVE)
  }

  protected getCompaniesByFieldValue(query: FirestoreQuery<Company>, field: string, value: any) {
    return query.collection(this.collection).where(field, '==', value)
  }

  protected getActiveCompaniesByFieldValue(query: FirestoreQuery<Company>, field: string, value: any) {
    return query.collection(this.collection).where(field, '==', value).where('status', '==', CompanyStatusEnum.ACTIVE)
  }

  protected getCompaniesByArrayContains(query: FirestoreQuery<Company>, arrayField: string, value: any) {
    return query.collection(this.collection).where(arrayField, 'array-contains', value)
  }

  protected getVendorCompanyBySevenFiftyIdQuery(query: FirestoreQuery<Company>, sevenFiftyVendorId: any) {
    return query.collection(this.collection)
      .where('type', '==', CompanyTypeEnum.VENDOR)
      .where('externalIds.sevenFifty', '==', sevenFiftyVendorId)
      .limit(1)
  }
}
